import enumTypes from 'constants/enumTypes';
import {
  BrandAutomationAccountFields,
  GamificationSpinDistributingTypes,
  PaymentRequestStatuses,
  GamificationStatuses,
  BetStates,
  AccessManagementRequestStatuses,
  PlayerStatuses,
  ExclusionLimitState,
  GamificationMachineTypes,
  GamificationContentGenerationTypes,
  RequestMethods,
  PaymentRefundStatuses,
  GamificationPrizeStatuses,
  KycDocumentStates,
  BrandVerificationDocumentTypes,
  AggregationCriteriaTypes,
  KycDocumentOptions,
  InboxStatuses,
  PushNotificationStatuses,
  PlayerBlockActions,
  DashboardIcons,
  DashboardWidgetChartType,
  BrandAutomationPeriodOptions,
  UnitStatuses,
  PlayerBonusStatuses,
  PromotionalItemStatus,
  BonusCommonStatuses,
  SmsProviders,
  Channels,
  Regulators,
  CumulativeTaxCalculationTypes,
  BrandExportCenterReportTypes,
  ActionStatuses,
  LobbyWidgetOperators,
  BulkBetCorrectionStatuses,
  NumberFormats,
  PlayerComplaintStatuses,
  PlayerBonusV2Statuses,
} from 'constants/enumTypes1';
import { title } from 'constants/tabs/title';
import { ColorKey } from './colors';

const playerStatusesIcons = {
  [PlayerStatuses.Active]: 'VerifiedIcon',
  [PlayerStatuses.Blocked]: 'TransactionIcon',
  [PlayerStatuses.BlockedForSport]: 'SportStatusIcon',
  [PlayerStatuses.BlockedForCasino]: 'BonusSettingsIcon',
  [PlayerStatuses.BlockedForWithdrawals]: 'ManageCurrencyIcon',
  [PlayerStatuses.BlockedForDeposits]: 'DepositsIcon',
  [PlayerStatuses.BlockedForUnverified]: 'BlockForUnverifiedIcon',
};

const playerActionStatusMap: Partial<Record<PlayerBlockActions, PlayerStatuses>> = {
  [PlayerBlockActions.SportBooks]: PlayerStatuses.BlockedForSport,
  [PlayerBlockActions.Casino]: PlayerStatuses.BlockedForCasino,
  [PlayerBlockActions.Deposit]: PlayerStatuses.BlockedForDeposits,
  [PlayerBlockActions.Withdrawals]: PlayerStatuses.BlockedForWithdrawals,
  [PlayerBlockActions.Unverified]: PlayerStatuses.BlockedForUnverified,
};

const paymentColors: Record<PaymentRequestStatuses, ColorKey> = {
  [PaymentRequestStatuses.Pending]: '#fa8c16',
  [PaymentRequestStatuses.InProcess]: '#fa8c16',
  [PaymentRequestStatuses.CancelledByUser]: '#e20a16',
  [PaymentRequestStatuses.CancelledByPlayer]: '#e20a16',
  [PaymentRequestStatuses.Failed]: '#e20a16',
  [PaymentRequestStatuses.Declined]: '#e20a16',
  [PaymentRequestStatuses.Frozen]: '#e20a16',
  [PaymentRequestStatuses.PayPending]: '#fa8c16',
  [PaymentRequestStatuses.Paid]: '#52c41a',
  [PaymentRequestStatuses.OnHold]: '#fa791c',
  [PaymentRequestStatuses.Initialized]: '#1890ff',
};

const betStateColors:Record<BetStates, ColorKey> = {
  [BetStates.New]: '#1890ff',
  [BetStates.Won]: '#52c41a',
  [BetStates.Lost]: '#e20a16',
  [BetStates.Cancelled]: '#e20a16',
  [BetStates.CashOut]: '#13c2c2',
};

const legalAgreementStatusesColors = {
  [enumTypes.legalAgreementVersionStatuses.Pending]: '#fa8c16',
  [enumTypes.legalAgreementVersionStatuses.Current]: '#1890ff',
  [enumTypes.legalAgreementVersionStatuses.Old]: '#e20a16',
};

const kycDocumentTypesOptions = {
  [BrandVerificationDocumentTypes.Passport]: KycDocumentOptions.Passport,
  [BrandVerificationDocumentTypes.IdCard]: KycDocumentOptions.IDCard,
  [BrandVerificationDocumentTypes.DrivingLicense]: KycDocumentOptions.DrivingLicense,
  [BrandVerificationDocumentTypes.Selfie]: KycDocumentOptions.Selfie,
  [BrandVerificationDocumentTypes.BankCard]: KycDocumentOptions.BankCard,
  [BrandVerificationDocumentTypes.UtilityBills]: KycDocumentOptions.UtilityBills,
  [BrandVerificationDocumentTypes.TaxBills]: KycDocumentOptions.TaxBills,
  [BrandVerificationDocumentTypes.BankStatement]: KycDocumentOptions.BankStatement,
  [BrandVerificationDocumentTypes.RentAgreement]: KycDocumentOptions.RentAgreement,
  [BrandVerificationDocumentTypes.InsuranceAgreement]: KycDocumentOptions.InsuranceAgreement,
  [BrandVerificationDocumentTypes.ResidencyCard]: KycDocumentOptions.ResidencyCard,
  [BrandVerificationDocumentTypes.PaymentMethod]: KycDocumentOptions.PaymentMethod,
  [BrandVerificationDocumentTypes.IncomeFromEmployment]: KycDocumentOptions.IncomeFromEmployment,
  [BrandVerificationDocumentTypes.IncomeFromInvestments]: KycDocumentOptions.IncomeFromInvestments,
  [BrandVerificationDocumentTypes.IncomeFromSales]: KycDocumentOptions.IncomeFromSales,
  [BrandVerificationDocumentTypes.ProofOfInheritance]: KycDocumentOptions.ProofOfInheritance,
  [BrandVerificationDocumentTypes.SpecialPermitOfStay]: KycDocumentOptions.SpecialPermitOfStay,
  [BrandVerificationDocumentTypes.TemporaryProtectionPermit]: KycDocumentOptions.TemporaryProtectionPermit,
  [BrandVerificationDocumentTypes.Other]: KycDocumentOptions.Other,
};

const kycDocumentStateColors:Record<KycDocumentStates, ColorKey> = {
  [KycDocumentStates.Pending]: '#fa8c16',
  [KycDocumentStates.Approved]: '#1890ff',
  [KycDocumentStates.Rejected]: '#e20a16',
  [KycDocumentStates.Verified]: '#52c41a',
  [KycDocumentStates.Archived]: '#8d939c',
  [KycDocumentStates.Expired]: '#722ed1',
  [KycDocumentStates.InProcess]: '#fa8c16',
  [KycDocumentStates.Deleted]: '#e20a16',
};

const bonusStatusesColors = {
  [enumTypes.bonusStatuses.Scheduled]: '#fa8c16',
  [enumTypes.bonusStatuses.Active]: '#1890ff',
  [enumTypes.bonusStatuses.Canceled]: '#e20a16',
  [enumTypes.bonusStatuses.PromotionCanceled]: '#e20a16',
  [enumTypes.bonusStatuses.Finished]: '#722ed1',
  [enumTypes.bonusStatuses.Pending]: '#fadb14',
  [enumTypes.bonusStatuses.PartialCancelled]: '#e20a16',
  [enumTypes.bonusStatuses.InProcess]: '#fadb14',
};

const playerBonusStatusesColors: Record<PlayerBonusStatuses, ColorKey> = {
  [PlayerBonusStatuses.New]: '#52c41a',
  [PlayerBonusStatuses.Active]: '#1890ff',
  [PlayerBonusStatuses.Finished]: '#722ed1',
  [PlayerBonusStatuses.Canceled]: '#e20a16',
  [PlayerBonusStatuses.Expired]: '#fa791c',
  [PlayerBonusStatuses.Pending]: '#fa8c16',
  [PlayerBonusStatuses.Paused]: '#fa791c',
};

const playerBonusV2StatusesColors: Record<PlayerBonusV2Statuses, ColorKey> = {
  [PlayerBonusV2Statuses.New]: '#52c41a',
  [PlayerBonusV2Statuses.Active]: '#1890ff',
  [PlayerBonusV2Statuses.Finished]: '#722ed1',
  [PlayerBonusV2Statuses.Canceled]: '#e20a16',
  [PlayerBonusV2Statuses.Expired]: '#fa791c',
  [PlayerBonusV2Statuses.Pending]: '#fa8c16',
};

const bonusCommonStatusesColors: Record<BonusCommonStatuses, ColorKey> = {
  [BonusCommonStatuses.Scheduled]: '#fa8c16',
  [BonusCommonStatuses.Active]: '#1890ff',
  [BonusCommonStatuses.Disabled]: '#e20a16',
  [BonusCommonStatuses.Finished]: '#722ed1',
};

const domainsStateColors = {
  [enumTypes.domainStatuses.Pending]: '#fa8c16',
  [enumTypes.domainStatuses.Active]: '#52c41a',
  [enumTypes.domainStatuses.InActive]: '#e20a16',
};

const conversionRouteKey = {
  [title.brandSetting.deposit]: 'depositLogs',
  [title.brandSetting.withdrawal]: 'withdrawalLogs',
  [title.brandSetting.other]: 'otherLogs',
};

const dashboardWidgetIcons = {
  [DashboardWidgetChartType.LineChart]: 'LineChartIcon',
  [DashboardWidgetChartType.BarChart]: 'BarChartIcon',
  [DashboardWidgetChartType.ColumnChart]: 'ColumnChartIcon',
  [DashboardWidgetChartType.PieChart]: 'PieChartIcon',
  [DashboardWidgetChartType.AreaChart]: 'AreaChartIcon',
  [DashboardWidgetChartType.StackedBarChart]: 'StackedBarChartIcon',
  [DashboardWidgetChartType.NumericWidget]: 'NumericWidgetIcon',
};

const dashboardIconsTitle = {
  [DashboardIcons.FinancialIcon]: 'mapping:financial',
  [DashboardIcons.AnalyticsIcon]: 'mapping:analytics',
  [DashboardIcons.PlayersIcon]: 'mapping:players',
  [DashboardIcons.CasinoIcon]: 'mapping:casino',
  [DashboardIcons.SportTennisIcon]: 'mapping:sport',
  [DashboardIcons.OtherIcon]: 'mapping:other',
  [DashboardIcons.BonusEngineIcon]: 'mapping:bonus',
};

const dashboardRouteKey = {
  Customers: 'player/aggregation',
  Betting: 'betReport/aggregation',
  Payments: 'paymentReport/aggregation',
  Bonuses: 'bonus/aggregation',
  FinancialTrends: 'betReport/aggregation/financial',
  CustomerTrends: 'player/aggregation/customers',
  Share: 'share',
};

const dashboardRouteKeyOptions = {
  [dashboardRouteKey.Customers]: 'mapping:customers',
  [dashboardRouteKey.Betting]: 'mapping:betting',
  [dashboardRouteKey.Payments]: 'mapping:payments',
  [dashboardRouteKey.Bonuses]: 'mapping:bonuses',
  [dashboardRouteKey.FinancialTrends]: 'mapping:financialTrends',
  [dashboardRouteKey.CustomerTrends]: 'mapping:customerTrends',
  [dashboardRouteKey.Share]: 'mapping:share',
};

const paymentRequestActionStatusMap = {
  StartProcess: PaymentRequestStatuses.InProcess,
  Cancel: PaymentRequestStatuses.CancelledByUser,
  Decline: PaymentRequestStatuses.Declined,
  Freeze: PaymentRequestStatuses.Frozen,
  Pay: PaymentRequestStatuses.Paid,
};

const playerBlockControlOptions = {
  Account: PlayerStatuses.Blocked,
  Sportsbook: PlayerStatuses.BlockedForSport,
  Casino: PlayerStatuses.BlockedForCasino,
  Withdrawals: PlayerStatuses.BlockedForWithdrawals,
  Deposits: PlayerStatuses.BlockedForDeposits,
  Unverified: PlayerStatuses.BlockedForUnverified,
};

const playerBlockedStatuses = {
  BlockAccount: PlayerStatuses.Blocked,
  BlockForSportsbook: PlayerStatuses.BlockedForSport,
  BlockForCasino: PlayerStatuses.BlockedForCasino,
  BlockForWithdrawals: PlayerStatuses.BlockedForWithdrawals,
  BlockForDeposits: PlayerStatuses.BlockedForDeposits,
  BlockedForUnverified: PlayerStatuses.BlockedForUnverified,
};

const smsProviders = {
  [SmsProviders.AfricasTalking]: 'AfricasTalking',
  [SmsProviders.Gateway]: 'Gateway',
  [SmsProviders.Twilio]: 'Twilio',
  [SmsProviders.Blackbox]: 'Blackbox',
  [SmsProviders.MobiPace]: 'MobiPace',
  [SmsProviders.Nikita]: 'Nikita',
  [SmsProviders.Dexatel]: 'Dexatel',
  [SmsProviders.Orchard]: 'Orchard',
  [SmsProviders.BudgetSMS]: 'BudgetSMS',
  [SmsProviders.SmartSMSSolutions]: 'SmartSMSSolutions',
  [SmsProviders.betaSMS]: 'Beta SMS',
  [SmsProviders.RPMMobile]: 'RPM Mobile',
  [SmsProviders.InfoBip]: 'Infobip',
  [SmsProviders.iCell]: 'iCell',
  [SmsProviders.Mobivate]: 'Mobivate',
  [SmsProviders.Wolkvox]: 'Wolkvox',
  [SmsProviders.BytePlus]: 'BytePlus',
  [SmsProviders.Laaffic]: 'Laaffic',
  [SmsProviders.LinkMobility]: 'LinkMobility',
  [SmsProviders.LotoBet]: 'LotoBet',
  [SmsProviders.UQMobile]: 'UQMobile',
};

const campaignConfigureModes = {
  bonus: 1,
  participant: 2,
  bonusContent: 3,
  campaignContent: 4,
  triggerCondition: 5,
};

const campaignBonusTypesIcons = {
  [enumTypes.allBonusTypes.FreeBet]: 'CasinoIcon',
  [enumTypes.allBonusTypes.WagerStandard]: 'StandardWagerIcon',
  [enumTypes.allBonusTypes.WagerReal]: 'RealWagerIcon',
  [enumTypes.allBonusTypes.SportWagering]: 'SportStandardWagerIcon',
  [enumTypes.allBonusTypes.SportFreeBet]: 'BetIcon',
  [enumTypes.allBonusTypes.SportRealWagering]: 'SportRealWagerIcon',
  [enumTypes.allBonusTypes.RealMoney]: 'DepositsAmountIcon',
  [enumTypes.allBonusTypes.FreeSpin]: 'FreeSpinIcon',
  [enumTypes.allBonusTypes.FreeAmount]: 'FreeAmountIcon',
};

const reasonsType = {
  [enumTypes.reasonsType.Default]: 'mapping:default',
  [enumTypes.reasonsType.Custom]: 'mapping:custom',
  [enumTypes.reasonsType.System]: 'mapping:system',
};

const sportsBookLayoutTypesIconMapping = {
  [enumTypes.sportsBookLayoutTypes.Standard]: 'StandardViewIcon',
  [enumTypes.sportsBookLayoutTypes.African]: 'AfricaIcon',
  [enumTypes.sportsBookLayoutTypes.Latin]: 'LatinIcon',
  [enumTypes.sportsBookLayoutTypes.Asian]: 'AsiaIcon',
  [enumTypes.sportsBookLayoutTypes.Modern]: 'ModernViewIcon',
};

const bonusTypes = {
  [enumTypes.bonuses.Casino]: {
    FreeBet: enumTypes.allBonusTypes.FreeBet,
    StandardWager: enumTypes.allBonusTypes.WagerStandard,
    RealWager: enumTypes.allBonusTypes.WagerReal,
  },
  [enumTypes.bonuses.Spin]: {
    FreeSpin: enumTypes.allBonusTypes.FreeSpin,
    FreeAmount: enumTypes.allBonusTypes.FreeAmount,
  },
  [enumTypes.bonuses.Sport]: {
    FreeBet: enumTypes.allBonusTypes.SportFreeBet,
    StandardWager: enumTypes.allBonusTypes.SportWagering,
    RealWager: enumTypes.allBonusTypes.SportRealWagering,
  },
  [enumTypes.bonuses.Special]: {
    RealMoney: enumTypes.allBonusTypes.RealMoney,
  },
};

const promoModes = {
  generalConfig: 'generalConfig',
  spinDistribution: 'spinDistribution',
  playingParticipants: 'playingParticipants',
  prize: 'prize',
  machine: 'machine',
  groupLimit: 'groupLimit',
  machineConfig: 'machineConfig',
  content: 'content',
  promotionContent: 'promotionContent',
  spinDistributionContent: 'spinDistributionContent',
  exchangeCenter: 'exchangeCenter',
  prizeContent: 'prizeContent',
};

const promotionMachineTypeIcon: Record<GamificationMachineTypes, string> = {
  [GamificationMachineTypes.Wheel]: 'WheelIcon',
  [GamificationMachineTypes.Grid]: 'GridPrizeIcon',
  [GamificationMachineTypes.Box]: 'MysteryBoxIcon',
  // [GamificationMachineType.Cube]: 'FreeSpinIcon',
};

const promotionContentTypeIcon = {
  [GamificationContentGenerationTypes.Promotion]: 'PromotionIcon',
  [GamificationContentGenerationTypes.Machine]: 'MysteryBoxIcon',
  [GamificationContentGenerationTypes.SpinDistribution]: 'SpinDistributionIcon',
  [GamificationContentGenerationTypes.Prize]: 'BonusesIcon',
};

export const promotionStatusColors: Record<GamificationStatuses, ColorKey> = {
  [GamificationStatuses.Draft]: '#8d939c',
  [GamificationStatuses.Scheduled]: '#fa8c16',
  [GamificationStatuses.Active]: '#1890ff',
  [GamificationStatuses.Finished]: '#722ed1',
};

const promotionalItemStatusColors: Record<PromotionalItemStatus, ColorKey> = {
  [PromotionalItemStatus.Active]: '#1890ff',
  [PromotionalItemStatus.Finished]: '#722ed1',
};

const sportsBookFeaturesWallets = {
  jackpots: 'mapping:jackpots',
  multiCashback: 'mapping:multiCashback',
  tournaments: 'mapping:tournaments',
  ultraCashback: 'mapping:ultraCashback',
  groupStage: 'mapping:groupStage',
  boreDraw: 'mapping:boreDraw',
};

const casinoFeaturesWallets = {
  jackpots: 'mapping:jackpots',
  tournaments: 'mapping:tournaments',
};

const exportReportStatesColors = {
  [enumTypes.exportReportStates.InQueue]: '#1890ff',
  [enumTypes.exportReportStates.Generating]: '#fadb14',
  [enumTypes.exportReportStates.Generated]: '#52c41a',
  [enumTypes.exportReportStates.Expired]: '#8d939c',
  [enumTypes.exportReportStates.Cancelled]: '#fa791c',
  [enumTypes.exportReportStates.Failed]: '#e20a16',
};

const playerPrizeStatusesColors: Record<GamificationPrizeStatuses, ColorKey> = {
  [GamificationPrizeStatuses.New]: '#fa8c16',
  [GamificationPrizeStatuses.Received]: '#52c41a',
  [GamificationPrizeStatuses.Cancelled]: '#e20a16',
  [GamificationPrizeStatuses.Pending]: '#fadb14',
};

const permissionSecurityLevelIcons = {
  [enumTypes.permissionSecurityLevel.Lowest]: 'PermissionLevel1Icon',
  [enumTypes.permissionSecurityLevel.Low]: 'PermissionLevel2Icon',
  [enumTypes.permissionSecurityLevel.Medium]: 'PermissionLevel3Icon',
  [enumTypes.permissionSecurityLevel.High]: 'PermissionLevel4Icon',
  [enumTypes.permissionSecurityLevel.Higher]: 'PermissionLevel5Icon',
  [enumTypes.permissionSecurityLevel.VeryHigh]: 'PermissionLevel6Icon',
  [enumTypes.permissionSecurityLevel.Maximum]: 'PermissionLevel7Icon',
};

const spinDistributionTriggerTypeAmount = {
  [GamificationSpinDistributingTypes.Deposit]: 'mapping:depositAmount',
  [GamificationSpinDistributingTypes.Bet]: 'mapping:betAmount',
};

const bulkBetCorrectionStatusesColors:Record<BulkBetCorrectionStatuses, ColorKey> = {
  [BulkBetCorrectionStatuses.InProgress]: '#fa791c',
  [BulkBetCorrectionStatuses.Failed]: '#e20a16',
  [BulkBetCorrectionStatuses.Success]: '#52c41a',
};

const limitStatusColors = {
  [enumTypes.limitStatus.Active]: '#1890ff',
  [enumTypes.limitStatus.Inactive]: '#8d939c',
  [enumTypes.limitStatus.Pending]: '#fa791c',
};

const exclusionStatusColors: Record<ExclusionLimitState, ColorKey> = {
  [ExclusionLimitState.Inactive]: '#8d939c',
  [ExclusionLimitState.Active]: '#1890ff',
};

const locales = {
  en: 'en',
  es: 'es',
  tr: 'tr',
  ru: 'ru',
  pt: 'pt-br',
  zh: 'zh-cn',
  ro: 'ro',
  bg: 'bg',
  el: 'el',
};

const promotionContentTypesModesMapping = {
  [GamificationContentGenerationTypes.Machine]: promoModes.content,
  [GamificationContentGenerationTypes.Promotion]: promoModes.promotionContent,
  [GamificationContentGenerationTypes.SpinDistribution]: promoModes.spinDistributionContent,
  [GamificationContentGenerationTypes.Prize]: promoModes.prizeContent,
};

const releaseNoteLabelsColor = {
  [enumTypes.releaseNoteLabels.Bug]: '#E20A16',
  [enumTypes.releaseNoteLabels.Improvement]: '#722ED1',
  [enumTypes.releaseNoteLabels.Feature]: '#52C41A',
};

const crossCampaignUsageStatusColors = {
  [enumTypes.crossCampaignSourceStatuses.Active]: '#1890ff',
  [enumTypes.crossCampaignSourceStatuses.Finished]: '#722ed1',
};

const autoRefreshPeriods = {
  10000: 'tenSec',
  30000: 'thirtySec',
  60000: 'oneMin',
  300000: 'fiveMin',
  600000: 'tenMin',
} as const;

const campaignModes = {
  generalConfig: 'generalConfig',
  participationGroups: 'participationGroups',
  bonuses: 'bonuses',
  triggerCondition: 'triggerCondition',
  bonusContent: 'bonusContent',
  campaignContent: 'campaignContent',
  units: 'units',
  offersContent: 'offersContent',
};

const referralModes = {
  view: 'view',
  viewReferees: 'viewReferees',
  viewAccumulatedCommissions: 'viewAccumulatedCommissions',
  viewTransferredCommissions: 'viewTransferredCommissions',
};

const campaignV2BonusTypesIcons = {
  [enumTypes.campaignV2Bonuses.FreeBetSport]: 'BetIcon',
  [enumTypes.campaignV2Bonuses.FreeBetCasino]: 'CasinoIcon',
  [enumTypes.campaignV2Bonuses.FreeGame]: 'FreeSpinIcon',
  [enumTypes.campaignV2Bonuses.WagerCombined]: 'RealWagerIcon',
  [enumTypes.campaignV2Bonuses.RealMoney]: 'FreeAmountIcon',
};

const campaignV2StatusColors = {
  [enumTypes.campaignV2Statuses.Draft]: '#8d939c',
  [enumTypes.campaignV2Statuses.Scheduled]: '#fa8c16',
  [enumTypes.campaignV2Statuses.Active]: '#1890ff',
  [enumTypes.campaignV2Statuses.Finished]: '#722ed1',
  [enumTypes.campaignV2Statuses.Pending]: '#fadb14',
  [enumTypes.campaignV2Statuses.InProcess]: '#fadb14',
};

const campaignV2BonusTypes = {
  [enumTypes.campaignV2BonusFamilies.FreeBet]: {
    FreeBetCasino: enumTypes.campaignV2Bonuses.FreeBetCasino,
    FreeBetSport: enumTypes.campaignV2Bonuses.FreeBetSport,
  },
};

const brandAutomationAccountFieldsLocaleKeys = {
  Citizenship: 'citizenship',
};

const brandAutomationAccountFieldLocaleMapping = {
  [BrandAutomationAccountFields.Citizenship]: brandAutomationAccountFieldsLocaleKeys.Citizenship,
};

const inboxMessageStatusColors: Record<InboxStatuses, ColorKey> = {
  [InboxStatuses.New]: '#52c41a',
  [InboxStatuses.SendByUser]: '#52c41a',
  [InboxStatuses.ReadByPlayer]: '#1890ff',
  [InboxStatuses.Deleted]: '#e20a16',
};

const notificationTemplateStatusColors = {
  [enumTypes.templateStatus.Ready]: '#1890ff',
  [enumTypes.templateStatus.Draft]: '#8d939c',
};

const messageStatusColors: Record<PushNotificationStatuses, ColorKey> = {
  [PushNotificationStatuses.Sent]: '#52c41a',
  [PushNotificationStatuses.Failed]: '#e20a16',
  [PushNotificationStatuses.Scheduled]: '#fa8c16',
  [PushNotificationStatuses.Sending]: '#fa791c',
  [PushNotificationStatuses.Preparing]: '#fadb14',
};

const actionStatusColors: Record<ActionStatuses, ColorKey> = {
  [ActionStatuses.Success]: '#52c41a',
  [ActionStatuses.Failed]: '#e20a16',
  [ActionStatuses.InProcess]: '#fa8c16',
};

const playerComplaintStatusColors: Record<PlayerComplaintStatuses, ColorKey> = {
  [PlayerComplaintStatuses.New]: '#52c41a',
  [PlayerComplaintStatuses.Replied]: '#fadb14',
};

const recentActionsStatusColors = {
  Successful: '#52c41a',
  Rejected: '#e20a16',
  Failed: '#e20a16',
};

const brandStatusColors = {
  [enumTypes.brandStatuses.Active]: '#52c41a',
  [enumTypes.brandStatuses.Disabled]: '#e20a16',
  [enumTypes.brandStatuses.ActivitiesDisabled]: '#fa8c16',
  [enumTypes.brandStatuses.VersionNamesDisabled]: '#fa791c',
  [enumTypes.brandStatuses.Maintenance]: '#fadb14',
};

const regulatoryBetEventCategoriesA = {
  [enumTypes.regulatoryBetEventCategoriesA.SportsEvents]: 'Sports Events',
  [enumTypes.regulatoryBetEventCategoriesA.VirtualEvents]: 'Virtual Events',
  [enumTypes.regulatoryBetEventCategoriesA.FantasySports]: 'Fantasy Sports',
  [enumTypes.regulatoryBetEventCategoriesA.OtherEvents]: 'Other Events',
};

const regulatoryPlayerLimitCategories = {
  [enumTypes.regulatoryPlayerLimitCategories.Other]: 'Other',
  [enumTypes.regulatoryPlayerLimitCategories.DailyTimeLimit]: 'Daily Time Limit',
  [enumTypes.regulatoryPlayerLimitCategories.WeeklyTimeLimit]: 'Weekly Time Limit',
  [enumTypes.regulatoryPlayerLimitCategories.MonthlyTimeLimit]: 'Monthly Time Limit',
  [enumTypes.regulatoryPlayerLimitCategories.DailyDepositLimit]: 'Daily Deposit Limit',
  [enumTypes.regulatoryPlayerLimitCategories.WeeklyDepositLimit]: 'Weekly Deposit Limit',
  [enumTypes.regulatoryPlayerLimitCategories.MonthlyDepositLimit]: 'Monthly Deposit Limit',
  [enumTypes.regulatoryPlayerLimitCategories.DailyLossLimit]: 'Daily Loss Limit',
  [enumTypes.regulatoryPlayerLimitCategories.WeeklyLossLimit]: 'Weekly Loss Limit',
  [enumTypes.regulatoryPlayerLimitCategories.MonthlyLossLimit]: 'Monthly Loss Limit',
};

const regulatoryGameCategories = {
  [enumTypes.regulatoryGameCategories.CasinoGamesLiveInStudio]: 'Live Casino Games',
  [enumTypes.regulatoryGameCategories.CasinoGames]: 'Casino Games',
  [enumTypes.regulatoryGameCategories.Other]: 'Other',
  [enumTypes.regulatoryGameCategories.PeerToPeerGames]: 'Peer To Peer Games',
};

const regulatoryBetEventTypes = {
  [enumTypes.regulatoryBetEventTypes.Main]: 'Main',
  [enumTypes.regulatoryBetEventTypes.Secondary]: 'Secondary',
};

const regulatoryBonusCategoryA = {
  [enumTypes.regulatoryBonusCategoryA.RedeemableBonusSchema]: 'Redeemable Bonus Schema',
  [enumTypes.regulatoryBonusCategoryA.NonRedeemableBonusSchema]: 'Non Redeemable Bonus Schema',
};

const regulatoryBonusCategoryB = {
  [enumTypes.regulatoryBonusCategoryB.Cashback]: 'Cashback',
  [enumTypes.regulatoryBonusCategoryB.FreeBet]: 'Free Bet',
  [enumTypes.regulatoryBonusCategoryB.FreeRound]: 'Free Round',
  [enumTypes.regulatoryBonusCategoryB.KambiFreeBet]: 'Kambi Free Bet',
  [enumTypes.regulatoryBonusCategoryB.Standard]: 'Standard',
  [enumTypes.regulatoryBonusCategoryB.Tournament]: 'Tournament',
  [enumTypes.regulatoryBonusCategoryB.TournamentTicket]: 'Tournament Ticket',
  [enumTypes.regulatoryBonusCategoryB.Wagering]: 'Wagering',
};

const aggregationCriteriaTypes = {
  [AggregationCriteriaTypes.Daily]: 'mapping:daily',
  [AggregationCriteriaTypes.Monthly]: 'mapping:monthly',
};

const bettingEventCategories = {
  [enumTypes.bettingEventCategories.PreGame]: 'Pre Game',
  [enumTypes.bettingEventCategories.Live]: 'Live',
  [enumTypes.bettingEventCategories.PreGameAndLive]: 'Pre Game And Live',
};

const bettingPerformanceBetTypes = {
  [enumTypes.bettingPerformanceBetTypes.Single]: 'Single',
  [enumTypes.bettingPerformanceBetTypes.Parlay]: 'Parlay',
  [enumTypes.bettingPerformanceBetTypes.Multiples]: 'Multiples',
  [enumTypes.bettingPerformanceBetTypes.Other]: 'Other',
};

const limitationTypesIconMapping = {
  [enumTypes.limitationTypes.LossLimit]: 'LossLimitIcon',
  [enumTypes.limitationTypes.Deposit]: 'DepositLimitIcon',
  [enumTypes.limitationTypes.GameTimeLimit]: 'GameplayIcon',
  [enumTypes.limitationTypes.Bet]: 'BetLimitIcon',
};

const regulators = {
  [Regulators.Cypriot]: 'cypriot',
  [Regulators.Greece]: 'greece',
  [Regulators.Romanian]: 'romanian',
  [Regulators.Estonia]: 'estonia',
  [Regulators.Bulgaria]: 'bulgaria',
  [Regulators.Brazil]: 'brazil',
  [Regulators.Portugal]: 'portugal',
};

const unitStatusColors: Record<UnitStatuses, ColorKey> = {
  [UnitStatuses.Draft]: '#8d939c',
  [UnitStatuses.Scheduled]: '#fa8c16',
  [UnitStatuses.Active]: '#1890ff',
  [UnitStatuses.Finished]: '#722ed1',
  [UnitStatuses.Canceled]: '#e20a16',
  [UnitStatuses.PartialCancelled]: '#e20a16',
};

const gridTypesIconMapping = {
  [enumTypes.lobbyWidgetGridTypes.Standard]: 'GridStandardIcon',
  [enumTypes.lobbyWidgetGridTypes.Advanced]: 'GridAdvancedIcon',
  [enumTypes.lobbyWidgetGridTypes.Vertical]: 'GridVerticalIcon',
  [enumTypes.lobbyWidgetGridTypes.Horizontal]: 'GridSpecificIcon',
};

const fileExtensions = {
  gif: '.gif',
  pdf: '.pdf',
  svg: '.svg',
  csv: '.csv',
  doc: '.doc',
  mp4: '.mp4',
  png: '.png',
  jpg: '.jpg',
  webp: '.webp',
  jpeg: '.jpeg',
  xlsx: '.xlsx',
  docx: '.docx',
  webm: '.webm',
  heic: '.heic',
  json: '.json',
} as const;

const playerMainInfoPositionMapping = {
  left: 1,
  right: 2,
  top: 3,
};

const booleanTypesWithAll = {
  No: 0,
  Yes: 1,
  All: 2,
};

const numberFormats = {
  [NumberFormats.EU]: 'EU Format',
  [NumberFormats.Space]: 'Space Format',
};

const countryCodeMapping = {
  Colombia: 'CO',
  Bulgaria: 'BG',
} as const;

const recentActionsRouteNames = {
  financials: {
    icon: 'AdjustBalanceIcon',
    resource: 'Players',
    action: 'BalanceAdjustmentByPlayerId',
  },
  bulkActions: {
    icon: 'AdjustBalanceIcon',
    resource: 'Balance Adjustment',
    action: 'GetBalanceAdjustmentsForBrand',
    query: {
      tab: 'balanceAdjustments',
    },
  },
  players: {
    icon: 'PlayersIcon',
    resource: 'Players',
  },
  withdrawals: {
    icon: 'WithdrawalsIcon',
    resource: 'Withdrawals',
  },
  deposits: {
    icon: 'DepositsIcon',
    resource: 'Deposits',
  },
  bonuses: {
    icon: 'WithdrawalsIcon',
    resource: 'Bonus',
  },
};

const actionMethods = {
  [RequestMethods.GET]: {
    label: 'view',
    method: RequestMethods.GET,
    color: '#1890ff',
    icon: 'PreviewIcon',
  },
  [RequestMethods.POST]: {
    label: 'create',
    method: RequestMethods.POST,
    color: '#52c41a',
    icon: 'SendEmailIcon',
  },
  [RequestMethods.PUT]: {
    label: 'update',
    method: RequestMethods.PUT,
    color: '#fa8c16',
    icon: 'StatusChangeIcon',
  },
  [RequestMethods.DELETE]: {
    label: 'delete',
    method: RequestMethods.DELETE,
    color: '#e20a16',
    icon: 'BrandDeleteIcon',
  },
};

const noteTypeColors = {
  [enumTypes.noteTypes.Risk]: '#e20a16',
  [enumTypes.noteTypes.Marketing]: '#ad2b9d',
  [enumTypes.noteTypes.Complaint]: '#e20a16',
  [enumTypes.noteTypes.Casino]: '#fa8c16',
  [enumTypes.noteTypes.Sport]: '#52c41a',
  [enumTypes.noteTypes.Other]: '#00AABE',
  [enumTypes.noteTypes.Security]: '#8D939C',
  [enumTypes.noteTypes.Migration]: '#353F4F',
  [enumTypes.noteTypes.Payment]: '#1890ff',
};

const requestTypeColors: Record<AccessManagementRequestStatuses, ColorKey> = {
  [AccessManagementRequestStatuses.Pending]: '#fa8c16',
  [AccessManagementRequestStatuses.Cancelled]: '#8d939c',
  [AccessManagementRequestStatuses.Approved]: '#52c41a',
  [AccessManagementRequestStatuses.Declined]: '#e20a16',
};

const refundStatusColors: Record<PaymentRefundStatuses, ColorKey> = {
  [PaymentRefundStatuses.NotRefunded]: '#8d939c',
  [PaymentRefundStatuses.Initiated]: '#fa8c16',
  [PaymentRefundStatuses.InProcess]: '#fa8c16',
  [PaymentRefundStatuses.Success]: '#52c41a',
  [PaymentRefundStatuses.Failed]: '#e20a16',
};

const ColorTypes = {
  Blue: 'blue',
  Success: 'success',
  Yellow: 'yellow',
  PinkPurple: 'pinkyPurple',
  Hover: 'hover',
  Danger: 'danger',
} as const;

const activeInactiveStates = {
  1: 'active',
  0: 'inactive',
} as const;

const enabledDisabledStates = {
  1: 'enabled',
  0: 'disabled',
} as const;

const maintenanceStates = {
  1: 'underMaintenance',
  0: 'noMaintenance',
} as const;

const bonusTypeProductTypeMapping = {
  [enumTypes.campaignV2Bonuses.FreeBetCasino]: [enumTypes.notificationProductTypes.Casino],
  [enumTypes.campaignV2Bonuses.FreeBetSport]: [enumTypes.notificationProductTypes.Sport],
  [enumTypes.campaignV2Bonuses.WagerCombined]: [enumTypes.notificationProductTypes.Sport, enumTypes.notificationProductTypes.Casino],
  [enumTypes.campaignV2Bonuses.FreeGame]: [enumTypes.notificationProductTypes.Casino],
  [enumTypes.campaignV2Bonuses.RealMoney]: [enumTypes.notificationProductTypes.Sport, enumTypes.notificationProductTypes.Casino, enumTypes.notificationProductTypes.Other],
};

const periodOptionsMapping = {
  [BrandAutomationPeriodOptions.Period]: 'period',
  [BrandAutomationPeriodOptions.TimeRange]: 'tool:timeRange',
};

const channelsIconMapping: Record<Channels, string> = {
  [Channels.SMS]: 'SmsIcon',
  [Channels.Email]: 'EmailIcon',
  [Channels.Inbox]: 'InboxIcon',
  [Channels.PushNotification]: 'PushNotificationIcon',
  [Channels.WhatsApp]: 'WhatsAppSimpleIcon',
};

const cumulativeTaxCalculationTypes = {
  [CumulativeTaxCalculationTypes.Relative]: 'brand:relative',
  [CumulativeTaxCalculationTypes.EntireAmount]: 'brand:entireAmount',
};

const brandExportCenterReportTypes: Record<BrandExportCenterReportTypes, string> = {
  [BrandExportCenterReportTypes.Casino]: 'casino',
  [BrandExportCenterReportTypes.Sport]: 'sport',
  [BrandExportCenterReportTypes.Deposit]: 'deposit',
  [BrandExportCenterReportTypes.Withdrawal]: 'withdrawal',
};

const limitTypeLabels = {
  [enumTypes.playerLimitPeriodTypes.UpcomingPeriod]: {
    limit1Day: 'hours24',
    limit7Days: 'days7',
    limit30Days: 'month1',
    limitAnnual: 'year1',
    limit3Days: 'days3',
  },
  [enumTypes.playerLimitPeriodTypes.Periodic]: {
    limit1Day: 'daily',
    limit7Days: 'weekly',
    limit30Days: 'monthly',
    limitAnnual: 'annually',
  },
  [enumTypes.playerLimitPeriodTypes.TriggerLastPeriod]: {
    limit1Day: 'lastTrigger24Hours',
    limit7Days: 'lastTrigger7Days',
    limit30Days: 'lastTrigger1Month',
    limitAnnual: 'lastTrigger1Year',
  },
} as const;

const lobbyWidgetOperators: Record<LobbyWidgetOperators, string> = {
  [LobbyWidgetOperators.In]: 'cms:in',
  [LobbyWidgetOperators.NotIn]: 'cms:notIn',
  [LobbyWidgetOperators.All]: 'all',
};

export {
  playerStatusesIcons,
  paymentColors,
  betStateColors,
  legalAgreementStatusesColors,
  kycDocumentTypesOptions,
  kycDocumentStateColors,
  bonusStatusesColors,
  domainsStateColors,
  bonusCommonStatusesColors,
  conversionRouteKey,
  dashboardWidgetIcons,
  dashboardRouteKey,
  dashboardRouteKeyOptions,
  dashboardIconsTitle,
  paymentRequestActionStatusMap,
  playerBlockControlOptions,
  playerBlockedStatuses,
  smsProviders,
  campaignConfigureModes,
  campaignBonusTypesIcons,
  reasonsType,
  playerBonusStatusesColors,
  playerBonusV2StatusesColors,
  sportsBookLayoutTypesIconMapping,
  bonusTypes,
  promoModes,
  promotionMachineTypeIcon,
  promotionContentTypeIcon,
  promotionalItemStatusColors,
  sportsBookFeaturesWallets,
  casinoFeaturesWallets,
  exportReportStatesColors,
  playerPrizeStatusesColors,
  permissionSecurityLevelIcons,
  bulkBetCorrectionStatusesColors,
  locales,
  spinDistributionTriggerTypeAmount,
  promotionContentTypesModesMapping,
  releaseNoteLabelsColor,
  crossCampaignUsageStatusColors,
  autoRefreshPeriods,
  campaignModes,
  campaignV2BonusTypesIcons,
  campaignV2StatusColors,
  campaignV2BonusTypes,
  brandAutomationAccountFieldsLocaleKeys,
  brandAutomationAccountFieldLocaleMapping,
  inboxMessageStatusColors,
  notificationTemplateStatusColors,
  playerComplaintStatusColors,
  recentActionsStatusColors,
  brandStatusColors,
  messageStatusColors,
  regulatoryPlayerLimitCategories,
  regulatoryBetEventCategoriesA,
  regulatoryGameCategories,
  regulatoryBetEventTypes,
  regulatoryBonusCategoryA,
  regulatoryBonusCategoryB,
  aggregationCriteriaTypes,
  bettingEventCategories,
  bettingPerformanceBetTypes,
  regulators,
  limitationTypesIconMapping,
  unitStatusColors,
  gridTypesIconMapping,
  fileExtensions,
  playerMainInfoPositionMapping,
  booleanTypesWithAll,
  numberFormats,
  countryCodeMapping,
  recentActionsRouteNames,
  actionMethods,
  noteTypeColors,
  actionStatusColors,
  limitStatusColors,
  exclusionStatusColors,
  ColorTypes,
  requestTypeColors,
  playerActionStatusMap,
  refundStatusColors,
  activeInactiveStates,
  enabledDisabledStates,
  bonusTypeProductTypeMapping,
  periodOptionsMapping,
  channelsIconMapping,
  cumulativeTaxCalculationTypes,
  brandExportCenterReportTypes,
  lobbyWidgetOperators,
  limitTypeLabels,
  maintenanceStates,
  referralModes,
};
